import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    TextField,
    IconButton,
    Chip,
    Switch,
    FormControlLabel,
    Divider,
    Badge,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    Button,
    AppBar,
    Toolbar,
} from '@mui/material';
import {
    Send as SendIcon,
    WhatsApp as WhatsAppIcon,
    Language as WebIcon,
    Instagram as InstagramIcon,
    Facebook as FacebookIcon,
    Email as EmailIcon,
    Telegram as TelegramIcon,
    SmartToy as AIIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    Menu as MenuIcon,
} from '@mui/icons-material';
import { supabase } from './supabaseClient';
import LeftNavDrawer from './components/LeftNavDrawer';
import DefaultPromptModal from './components/DefaultPromptModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Channel icon mapping
const getChannelIcon = (channel) => {
    if (!channel) return <WebIcon />;
    const lowerChannel = channel.toLowerCase();
    if (lowerChannel.includes('whatsapp')) return <WhatsAppIcon sx={{ color: '#25D366' }} />;
    if (lowerChannel.includes('instagram')) return <InstagramIcon sx={{ color: '#E4405F' }} />;
    if (lowerChannel.includes('facebook')) return <FacebookIcon sx={{ color: '#1877F2' }} />;
    if (lowerChannel.includes('email')) return <EmailIcon sx={{ color: '#EA4335' }} />;
    if (lowerChannel.includes('telegram')) return <TelegramIcon sx={{ color: '#0088cc' }} />;
    if (lowerChannel.includes('customwidget')) return <WebIcon sx={{ color: '#4F46E5' }} />;
    return <WebIcon sx={{ color: '#4CAF50' }} />;
};

// Channel name cleanup
const getChannelName = (channel) => {
    if (!channel) return 'Web Widget';
    if (channel.toLowerCase().includes('customwidget')) return 'Custom Widget';
    return channel.replace('Channel::', '').replace(/([A-Z])/g, ' $1').trim();
};

function AgentInbox({ handleLogout }) {
    const navigate = useNavigate();

    // State
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [channelFilter, setChannelFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [navSidebarOpen, setNavSidebarOpen] = useState(false);
    const [defaultPromptModalOpen, setDefaultPromptModalOpen] = useState(false);
    const [userId, setUserId] = useState(null);

    const messagesEndRef = useRef(null);

    // Get auth token
    const getAuthToken = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token;
    }, []);

    // Get userId on mount
    useEffect(() => {
        const fetchUserId = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) setUserId(user.id);
        };
        fetchUserId();
    }, []);

    // Fetch conversations - unified from widget_sessions (includes social channels)
    const fetchConversations = useCallback(async () => {
        try {
            setLoading(true);
            const token = await getAuthToken();
            if (!token) {
                navigate('/login');
                return;
            }

            // Build query params for filtering
            const params = new URLSearchParams();
            if (channelFilter) params.append('channel_filter', channelFilter);
            if (statusFilter) params.append('status_filter', statusFilter);
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Fetch from unified widget endpoint (now includes all sources)
            const response = await axios.get(
                `${BACKEND_URL}/widget/agent/conversations${queryString}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Conversations already include source field from backend
            const allConversations = (response.data.conversations || []).sort((a, b) => {
                const aTime = new Date(a.last_activity_at || a.created_at || 0);
                const bTime = new Date(b.last_activity_at || b.created_at || 0);
                return bTime - aTime;
            });

            setConversations(allConversations);
            setError('');
        } catch (err) {
            console.error('Error fetching conversations:', err);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    }, [getAuthToken, navigate, channelFilter, statusFilter]);

    // Fetch messages for selected conversation
    const fetchMessages = useCallback(async (conversation) => {
        try {
            setMessagesLoading(true);
            const token = await getAuthToken();

            // All conversations use widget messages endpoint (unified in widget_sessions)
            const messagesUrl = `${BACKEND_URL}/widget/agent/conversations/${conversation.rag_session_id}/messages`;

            const response = await axios.get(
                messagesUrl,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMessages(response.data.messages || []);
        } catch (err) {
            console.error('Error fetching messages:', err);
            setError('Failed to load messages');
        } finally {
            setMessagesLoading(false);
        }
    }, [getAuthToken]);

    // Toggle AI auto-response
    const toggleAI = async (conversation, enabled) => {
        try {
            const token = await getAuthToken();

            // All conversations use widget endpoint (unified in widget_sessions)
            const toggleUrl = `${BACKEND_URL}/widget/agent/conversations/${conversation.rag_session_id}/ai-toggle`;

            await axios.patch(
                toggleUrl,
                { ai_auto_response: enabled },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local state
            setConversations(prev => prev.map(conv =>
                conv.rag_session_id === conversation.rag_session_id
                    ? { ...conv, ai_auto_response: enabled }
                    : conv
            ));

            if (selectedConversation?.rag_session_id === conversation.rag_session_id) {
                setSelectedConversation(prev => ({ ...prev, ai_auto_response: enabled }));
            }
        } catch (err) {
            console.error('Error toggling AI:', err);
            setError('Failed to toggle AI response');
        }
    };

    // Send reply
    const sendReply = async () => {
        if (!replyText.trim() || !selectedConversation) return;

        try {
            setSending(true);
            const token = await getAuthToken();

            // All conversations use widget endpoint (unified in widget_sessions)
            const replyUrl = `${BACKEND_URL}/widget/agent/conversations/${selectedConversation.rag_session_id}/reply`;

            await axios.post(
                replyUrl,
                { content: replyText },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Don't add message locally - Realtime will pick it up from chat_messages table
            // This prevents duplicate messages from appearing

            setReplyText('');
        } catch (err) {
            console.error('Error sending reply:', err);
            setError('Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    // Approve pending message (send to customer)
    const handleApproveMessage = async (messageId) => {
        if (!selectedConversation) return;

        try {
            const token = await getAuthToken();

            // Call backend to approve and send the message
            await axios.post(
                `${BACKEND_URL}/widget/agent/messages/${messageId}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Refresh messages to show updated status
            await fetchMessages(selectedConversation);
        } catch (err) {
            console.error('Error approving message:', err);
            setError('Failed to send message');
        }
    };

    // Reject pending message (discard it)
    const handleRejectMessage = async (messageId) => {
        if (!selectedConversation) return;

        try {
            const token = await getAuthToken();

            // Call backend to reject the message
            await axios.post(
                `${BACKEND_URL}/widget/agent/messages/${messageId}/reject`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Refresh messages to show updated status (or remove the message)
            await fetchMessages(selectedConversation);
        } catch (err) {
            console.error('Error rejecting message:', err);
            setError('Failed to reject message');
        }
    };

    // Initial load only
    useEffect(() => {
        fetchConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on mount, not on every fetchConversations change

    // Use ref to access selectedConversation in Realtime callback without causing reconnection
    const selectedConversationRef = useRef(selectedConversation);
    useEffect(() => {
        selectedConversationRef.current = selectedConversation;
    }, [selectedConversation]);

    // Supabase Realtime subscription - stable channel, uses refs
    useEffect(() => {
        console.log('[Realtime] Setting up subscription...');

        const channel = supabase
            .channel('agent-inbox-changes')
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'chat_messages' },
                (payload) => {
                    console.log('[Realtime] chat_messages event:', payload.eventType, payload);

                    if (payload.eventType === 'INSERT') {
                        const newMessage = payload.new;
                        const currentConv = selectedConversationRef.current;

                        // If viewing this conversation, append the message
                        if (currentConv && newMessage.session_id === currentConv.rag_session_id) {
                            setMessages(prev => {
                                if (prev.some(m => m.id === newMessage.id)) return prev;
                                console.log('[Realtime] Appending new message to chat');
                                return [...prev, {
                                    id: newMessage.id,
                                    role: newMessage.role,
                                    content: newMessage.content,
                                    created_at: newMessage.created_at,
                                    metadata: newMessage.metadata
                                }];
                            });
                        }

                        // Update last_activity in conversation list
                        setConversations(prev => prev.map(conv =>
                            conv.rag_session_id === newMessage.session_id
                                ? { ...conv, last_activity_at: newMessage.created_at }
                                : conv
                        ));
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'chatwoot_sessions' },
                (payload) => {
                    console.log('[Realtime] chatwoot_sessions event:', payload.eventType, payload);

                    if (payload.eventType === 'INSERT') {
                        const newSession = payload.new;
                        console.log('[Realtime] New conversation, prepending to list');
                        setConversations(prev => {
                            if (prev.some(c => c.id === newSession.id)) return prev;
                            return [newSession, ...prev];
                        });
                    } else if (payload.eventType === 'UPDATE') {
                        const updated = payload.new;
                        console.log('[Realtime] Conversation updated, merging');
                        setConversations(prev => prev.map(conv =>
                            conv.id === updated.id ? { ...conv, ...updated } : conv
                        ));

                        const currentConv = selectedConversationRef.current;
                        if (currentConv?.id === updated.id) {
                            setSelectedConversation(prev => ({ ...prev, ...updated }));
                        }
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'widget_sessions' },
                (payload) => {
                    console.log('[Realtime] widget_sessions event:', payload.eventType, payload);

                    if (payload.eventType === 'INSERT') {
                        const newSession = payload.new;
                        console.log('[Realtime] New widget conversation, prepending to list');
                        setConversations(prev => {
                            if (prev.some(c => c.id === newSession.id)) return prev;
                            return [{
                                ...newSession,
                                source: 'widget',
                                channel: 'Channel::CustomWidget',
                                contact_name: newSession.visitor_name || newSession.visitor_id?.substring(0, 8) || 'Visitor'
                            }, ...prev];
                        });
                    } else if (payload.eventType === 'UPDATE') {
                        const updated = payload.new;
                        console.log('[Realtime] Widget conversation updated, merging');
                        setConversations(prev => prev.map(conv =>
                            conv.id === updated.id && conv.source === 'widget'
                                ? { ...conv, ...updated }
                                : conv
                        ));

                        const currentConv = selectedConversationRef.current;
                        if (currentConv?.id === updated.id && currentConv?.source === 'widget') {
                            setSelectedConversation(prev => ({ ...prev, ...updated }));
                        }
                    }
                }
            )
            .subscribe((status, err) => {
                console.log('[Realtime] Subscription status:', status, err || '');
                if (status === 'SUBSCRIBED') {
                    console.log('[Realtime] ✅ Successfully subscribed to changes!');
                }
                if (status === 'CHANNEL_ERROR') {
                    console.error('[Realtime] ❌ Channel error:', err);
                }
            });

        return () => {
            console.log('[Realtime] Cleaning up subscription');
            supabase.removeChannel(channel);
        };
    }, []); // Empty deps - stable channel that doesn't recreate

    // Smart background sync - silently merge new data without flickering
    useEffect(() => {
        const syncConversations = async () => {
            try {
                const token = await getAuthToken();
                if (!token) return;

                // Fetch from both sources
                const [chatwootRes, widgetRes] = await Promise.allSettled([
                    axios.get(`${BACKEND_URL}/chatwoot/agent/conversations`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`${BACKEND_URL}/widget/agent/conversations`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);

                const chatwootConvs = chatwootRes.status === 'fulfilled'
                    ? (chatwootRes.value.data.conversations || []).map(c => ({ ...c, source: 'chatwoot' }))
                    : [];

                const widgetConvs = widgetRes.status === 'fulfilled'
                    ? (widgetRes.value.data.conversations || []).map(c => ({
                        ...c,
                        source: c.source || 'widget',
                        channel: c.channel || 'Channel::CustomWidget',
                        contact_name: c.contact_name || c.visitor_name || c.visitor_id?.substring(0, 16) || 'Visitor'
                    }))
                    : [];

                const newConversations = [...chatwootConvs, ...widgetConvs];

                // Replace conversations with server state - ensures deleted items are removed
                setConversations(newConversations.sort((a, b) => {
                    const aTime = new Date(a.last_activity_at || a.created_at || 0);
                    const bTime = new Date(b.last_activity_at || b.created_at || 0);
                    return bTime - aTime;
                }));
            } catch (err) {
                // Silent fail - don't show errors for background sync
                console.log('[Sync] Background sync failed:', err.message);
            }
        };

        // Sync every 5 seconds
        const syncInterval = setInterval(syncConversations, 5000);
        return () => clearInterval(syncInterval);
    }, [getAuthToken]);

    // Sync messages for selected conversation
    useEffect(() => {
        if (!selectedConversation) return;

        // Clear previous messages when conversation changes
        setMessages([]);

        // Determine if this is a widget/social channel (instagram, facebook, etc) or Chatwoot
        // Widget-like sources use rag_session_id, Chatwoot uses chatwoot_conversation_id
        const isWidgetOrSocial = selectedConversation.source !== 'chatwoot';

        const conversationId = isWidgetOrSocial
            ? selectedConversation.rag_session_id
            : selectedConversation.chatwoot_conversation_id;

        const syncMessages = async () => {
            try {
                const token = await getAuthToken();
                if (!token) return;

                // Determine the correct endpoint based on conversation source
                let messagesUrl;
                if (isWidgetOrSocial) {
                    // Widget/Instagram/Facebook/etc conversation - use rag_session_id
                    if (!selectedConversation.rag_session_id) {
                        console.log('[Sync] No rag_session_id for widget/social conversation');
                        return;
                    }
                    messagesUrl = `${BACKEND_URL}/widget/agent/conversations/${selectedConversation.rag_session_id}/messages`;
                } else {
                    // Chatwoot conversation - check for valid ID
                    if (!selectedConversation.chatwoot_conversation_id) {
                        console.log('[Sync] No chatwoot_conversation_id for chatwoot conversation');
                        return;
                    }
                    messagesUrl = `${BACKEND_URL}/chatwoot/agent/conversations/${selectedConversation.chatwoot_conversation_id}/messages`;
                }

                const response = await axios.get(
                    messagesUrl,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const newMessages = response.data.messages || [];

                // Replace messages entirely on first load, merge on subsequent syncs
                setMessages(prev => {
                    if (prev.length === 0) {
                        // First load - replace all
                        return newMessages;
                    }
                    // Subsequent syncs - merge only truly new messages
                    const existingIds = new Set(prev.map(m => m.id));
                    const newItems = newMessages.filter(m => !existingIds.has(m.id));

                    if (newItems.length > 0) {
                        console.log(`[Sync] Found ${newItems.length} new messages`);
                        return [...prev, ...newItems];
                    }
                    return prev;
                });
            } catch (err) {
                console.log('[Sync] Message sync failed:', err.message);
            }
        };

        // Initial load
        syncMessages();

        // Sync every 3 seconds
        const syncInterval = setInterval(syncMessages, 3000);
        return () => clearInterval(syncInterval);
    }, [selectedConversation?.id, selectedConversation?.source, getAuthToken]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Handle key press for sending
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendReply();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Top AppBar */}
            <AppBar position="static" color="default" elevation={1}>
                <Toolbar variant="dense">
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                        Customer Inbox
                    </Typography>
                    <IconButton edge="end" color="inherit" aria-label="menu" onClick={() => setNavSidebarOpen(true)}>
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Main Content Area */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Left Panel - Conversation List */}
                <Paper sx={{ width: 350, display: 'flex', flexDirection: 'column', borderRadius: 0, borderRight: 1, borderColor: 'divider' }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Customer Inbox</Typography>
                            <IconButton onClick={fetchConversations} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Box>

                        {/* Filters */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Channel</InputLabel>
                                <Select
                                    value={channelFilter}
                                    label="Channel"
                                    onChange={(e) => setChannelFilter(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="Channel::WebWidget">Web Widget</MenuItem>
                                    <MenuItem value="Channel::Whatsapp">WhatsApp</MenuItem>
                                    <MenuItem value="Channel::Instagram">Instagram</MenuItem>
                                    <MenuItem value="Channel::FacebookPage">Facebook</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="open">Open</MenuItem>
                                    <MenuItem value="resolved">Resolved</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Conversation List */}
                    <List sx={{ overflow: 'auto', flex: 1 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : conversations.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography color="text.secondary">No conversations found</Typography>
                            </Box>
                        ) : (
                            conversations.map((conv) => (
                                <ListItem
                                    key={conv.id}
                                    button
                                    selected={selectedConversation?.id === conv.id}
                                    onClick={() => setSelectedConversation(conv)}
                                    sx={{
                                        borderBottom: '1px solid #eee',
                                        '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Badge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            badgeContent={getChannelIcon(conv.channel)}
                                        >
                                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                {conv.contact_name?.[0]?.toUpperCase() || '?'}
                                            </Avatar>
                                        </Badge>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {conv.contact_name || 'Unknown Contact'}
                                                </Typography>
                                                {conv.ai_auto_response === false && (
                                                    <Chip label="Manual" size="small" color="warning" sx={{ height: 18 }} />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {getChannelName(conv.channel)} • {conv.status || 'pending'}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))
                        )}
                    </List>
                </Paper>

                {/* Center Panel - Chat */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>
                            {error}
                        </Alert>
                    )}

                    {selectedConversation ? (
                        <>
                            {/* Chat Header */}
                            <Paper sx={{ p: 2, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            {selectedConversation.contact_name?.[0]?.toUpperCase() || '?'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6">
                                                {selectedConversation.contact_name || 'Unknown Contact'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getChannelIcon(selectedConversation.channel)}
                                                <Typography variant="caption" color="text.secondary">
                                                    {getChannelName(selectedConversation.channel)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* AI Toggle */}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={selectedConversation.ai_auto_response !== false}
                                                onChange={(e) => toggleAI(selectedConversation, e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {selectedConversation.ai_auto_response !== false ? (
                                                    <>
                                                        <AIIcon fontSize="small" color="primary" />
                                                        <Typography variant="body2">AI Active</Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <PersonIcon fontSize="small" color="warning" />
                                                        <Typography variant="body2" color="warning.main">Manual Mode</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Paper>

                            {/* Messages */}
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
                                {messagesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : messages.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', p: 4 }}>
                                        <Typography color="text.secondary">No messages yet</Typography>
                                    </Box>
                                ) : (
                                    messages.map((msg, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: msg.role === 'user' ? 'flex-start' : 'flex-end',
                                                mb: 1,
                                            }}
                                        >
                                            <Paper
                                                sx={{
                                                    p: 1.5,
                                                    maxWidth: '70%',
                                                    // Pending messages get amber background
                                                    bgcolor: msg.approval_status === 'pending'
                                                        ? '#fff8e1'
                                                        : (msg.role === 'user' ? 'grey.100' : 'primary.main'),
                                                    color: msg.approval_status === 'pending'
                                                        ? 'text.primary'
                                                        : (msg.role === 'user' ? 'text.primary' : 'white'),
                                                    borderRadius: 2,
                                                    border: msg.approval_status === 'pending' ? '2px solid #ffc107' : 'none',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {msg.content}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                                                    {new Date(msg.created_at).toLocaleTimeString()}
                                                    {msg.metadata?.source === 'agent_reply' && ' • Agent'}
                                                </Typography>

                                                {/* Pending approval indicator and buttons */}
                                                {msg.approval_status === 'pending' && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: '#f57c00',
                                                                fontWeight: 500,
                                                                mb: 1
                                                            }}
                                                        >
                                                            ⏳ Approval waiting
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<SendIcon />}
                                                                onClick={() => handleApproveMessage(msg.id)}
                                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                            >
                                                                Send to Customer
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() => handleRejectMessage(msg.id)}
                                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Box>
                                    ))
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Reply Input */}
                            <Paper sx={{ p: 2, borderRadius: 0, borderTop: 1, borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        placeholder="Type your reply..."
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={sending}
                                        size="small"
                                    />
                                    <IconButton
                                        color="primary"
                                        onClick={sendReply}
                                        disabled={!replyText.trim() || sending}
                                    >
                                        {sending ? <CircularProgress size={24} /> : <SendIcon />}
                                    </IconButton>
                                </Box>
                                {selectedConversation.ai_auto_response !== false && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        💡 AI is handling this conversation. Toggle off to reply manually.
                                    </Typography>
                                )}
                            </Paper>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography color="text.secondary">Select a conversation to view messages</Typography>
                        </Box>
                    )}
                </Box>

                {/* Right Panel - Contact Info */}
                {selectedConversation && (
                    <Paper sx={{ width: 280, p: 2, borderRadius: 0 }}>
                        <Typography variant="h6" gutterBottom>Contact Info</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Name</Typography>
                            <Typography>{selectedConversation.contact_name || 'Unknown'}</Typography>
                        </Box>

                        {selectedConversation.contact_email && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <Typography>{selectedConversation.contact_email}</Typography>
                            </Box>
                        )}

                        {selectedConversation.contact_phone_number && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography>{selectedConversation.contact_phone_number}</Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Channel</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                {getChannelIcon(selectedConversation.channel)}
                                <Typography>{getChannelName(selectedConversation.channel)}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Chip
                                label={selectedConversation.status || 'pending'}
                                size="small"
                                color={selectedConversation.status === 'resolved' ? 'success' : 'default'}
                                sx={{ mt: 0.5 }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary">Conversation ID</Typography>
                            <Typography variant="body2">#{selectedConversation.chatwoot_conversation_id}</Typography>
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* Right Navigation Drawer */}
            <LeftNavDrawer
                anchor="right"
                open={navSidebarOpen}
                onClose={() => setNavSidebarOpen(false)}
                onLogout={handleLogout}
                onOpenFiles={() => navigate('/files')}
                onOpenFAQManagement={() => navigate('/faq')}
                onOpenChatwootSettings={() => navigate('/chat')} // Redirect to chat for settings if needed, or implement separate page
                onOpenEcommerceIntegrations={() => navigate('/my-stores')}
                onOpenUpload={() => navigate('/upload')}
                onOpenUsageBilling={() => navigate('/billing')}
                onOpenCalCom={() => {
                    const bookingUrl = process.env.REACT_APP_BOOKING_URL;
                    if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener,noreferrer');
                }}
                onOpenDefaultPrompt={() => setDefaultPromptModalOpen(true)}
                hideChat={true}
            />

            {/* Default System Prompt Modal */}
            <DefaultPromptModal
                open={defaultPromptModalOpen}
                onClose={() => setDefaultPromptModalOpen(false)}
                userId={userId}
            />
        </Box>
    );
}

export default AgentInbox;
